/**
 * Bottle alerts in the browser: registering the service worker (/sw.js),
 * subscribing this device to Web Push, and unsubscribing it. The server side
 * is routes/push.ts; what Settings shows is decided in push-state.ts, and a
 * tapped alert is handled in push-clicks.ts.
 */
import { captureError, displayMode, track } from "./analytics.ts";
import {
  forgetSubscription,
  keyBytes,
  pushSupport,
  type PushSupport,
  sameKey,
} from "./push-state.ts";

const WORKER_URL = "/sw.js";

/**
 * A failure on this device, not a request: a declined prompt, or a browser
 * that can't subscribe. Not counted as a failed write (main.tsx).
 */
export class DeviceError extends Error {}

export const BLOCKED_MESSAGE =
  "Notifications are blocked for Babia. Allow them in this browser's settings, then try again.";

/** Whether this browser can get bottle alerts, or what it needs first. */
export function devicePushSupport(): PushSupport {
  if (typeof window === "undefined") return "unsupported";
  const hasPush =
    "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  // iPadOS reports itself as a Mac; touch tells them apart.
  const isIos =
    /iP(hone|ad|od)/.test(navigator.userAgent) ||
    (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);
  return pushSupport({ hasPush, isIos, standalone: displayMode() === "standalone" });
}

export function notificationPermission(): NotificationPermission | null {
  return typeof Notification === "undefined" ? null : Notification.permission;
}

/** This browser's push subscription, if it has one. */
export async function deviceSubscription(): Promise<PushSubscription | null> {
  if (devicePushSupport() !== "ok") return null;
  const registration = await navigator.serviceWorker.getRegistration("/");
  return (await registration?.pushManager.getSubscription()) ?? null;
}

/** Whether a subscription was made with the server's current key, so it gets alerts. */
export function hasKey(subscription: PushSubscription, publicKey: string): boolean {
  return sameKey(subscription.options.applicationServerKey, keyBytes(publicKey));
}

/** What the server stores of a subscription (POST /api/push/subscription). */
function subscriptionBody(subscription: PushSubscription) {
  const { endpoint, keys } = subscription.toJSON();
  if (!endpoint || !keys?.p256dh || !keys.auth) throw new Error("no endpoint or keys");
  return { endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth } };
}

/**
 * Subscribe this device. `permission` is `Notification.requestPermission()`,
 * called by the tap handler itself: Safari asks only inside a tap, before any
 * other await. `currentKey` then fetches the server's key, never a cached
 * one: a subscription made with an old key gets no alerts. A subscription
 * made with an older key is replaced, and `forget` tells the server to drop
 * its old address.
 */
export async function turnOnPush(
  permission: Promise<NotificationPermission>,
  currentKey: () => Promise<string>,
  forget: (endpoint: string) => Promise<unknown>,
) {
  const answer = await permission;
  track("push permission answered", { accepted: answer === "granted" });
  if (answer !== "granted")
    throw new DeviceError(
      answer === "denied" ? BLOCKED_MESSAGE : "Allow notifications to get alerts.",
    );
  const publicKey = await currentKey();
  try {
    await navigator.serviceWorker.register(WORKER_URL, { scope: "/", updateViaCache: "none" });
    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();
    if (subscription && !hasKey(subscription, publicKey)) {
      await forgetSubscription(subscription, forget);
      subscription = null;
    }
    subscription ??= await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyBytes(publicKey),
    });
    return subscriptionBody(subscription);
  } catch (error) {
    captureError(error, { area: "push" });
    throw new DeviceError("This browser couldn't set up notifications. Try again.", {
      cause: error,
    });
  }
}

/**
 * Turn this device's alerts off: the browser unsubscribes, then `forget` tells
 * the server (routes/push.ts). See `forgetSubscription` for why in that order.
 * Either one stops the alerts; it throws only when neither happened.
 */
export async function turnOffPush(forget: (endpoint: string) => Promise<unknown>): Promise<void> {
  const subscription = await deviceSubscription();
  if (!subscription) return;
  const { unsubscribed, forgotten } = await forgetSubscription(subscription, forget);
  if (!unsubscribed && !forgotten)
    throw new Error("Babia couldn't turn alerts off. Check the connection and try again.");
}
